import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SettingOutlined,
  LogoutOutlined,
  DownOutlined,
  SearchOutlined,
  MenuOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { navigationItems, NavItem } from '../../router/navigationConfig';
import { LanguageSelector } from '../shared/LanguageSelector';
import { ThemeSelector } from '../shared/ThemeSelector';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSettings } from '../../contexts/SettingsContext';
import CommandPalette from './CommandPalette';

interface TopNavigationProps {
  currentPage: string;
}

// Mapea el path de cada item de navegación a su clave de settings (menu.items).
const MENU_KEY_BY_PATH: Record<string, string> = {
  '/dashboard': 'overview',
  '/resources': 'infrastructure',
  '/deploy-apps': 'deployApps',
  '/virtual-machines': 'virtualMachines',
  '/databases': 'databases',
  '/chatbot-ia': 'chatbotIA',
  '/marketplace': 'marketplace',
  '/orders': 'orders',
};

const TopNavigation: React.FC<TopNavigationProps> = ({ currentPage }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const { isMenuItemVisible } = useSettings();
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [openSubmenu, setOpenSubmenu] = React.useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileSubmenu, setMobileSubmenu] = React.useState<number | null>(null);
  const [paletteOpen, setPaletteOpen] = React.useState(false);

  // ⌘K / Ctrl+K abre la paleta desde cualquier lugar.
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Bloquear scroll del body cuando el drawer mobile está abierto.
  React.useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    try {
      setShowUserMenu(false);
      setMobileOpen(false);
      await logout();
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/');
    }
  };

  const isItemActive = (item: (typeof navigationItems)[number]) =>
    currentPage === item.path.split('/')[1] ||
    (!!item.submenu && item.submenu.some((sub) => currentPage === sub.path.split('/')[1]));

  // Oculta items (y subitems) que el admin deshabilitó. Un submenú sin hijos
  // visibles también se oculta. `missing = visible` por defecto.
  const isNavItemVisible = (item: NavItem): boolean => {
    const key = MENU_KEY_BY_PATH[item.path];
    if (key && !isMenuItemVisible(key)) return false;
    return true;
  };
  const visibleNavigationItems = navigationItems
    .filter(isNavItemVisible)
    .map((item) =>
      item.submenu ? { ...item, submenu: item.submenu.filter(isNavItemVisible) } : item
    )
    .filter((item) => !item.submenu || item.submenu.length > 0);

  const navigateAndCloseMobile = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    setMobileSubmenu(null);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Izquierda: logo + nav desktop */}
            <div className="flex items-center gap-6">
              <div
                className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate('/dashboard')}
              >
                <img
                  src={theme === 'dark' ? '/logo-dark.png' : '/logo.png'}
                  alt="Kubiy Cloud"
                  className="h-20 w-auto"
                />
              </div>

              {/* Nav desktop (oculta en mobile) */}
              <div className="hidden lg:flex items-center gap-1">
                {visibleNavigationItems.map((item, index) => {
                  const isActive = isItemActive(item);
                  const isSubmenuOpen = openSubmenu === index;

                  if (item.submenu) {
                    return (
                      <div key={index} className="relative">
                        <button
                          type="button"
                          onClick={() => setOpenSubmenu(isSubmenuOpen ? null : index)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive
                              ? 'text-black dark:text-white bg-gray-100 dark:bg-gray-800'
                              : 'text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          {item.icon}
                          <span>{item.label}</span>
                          <DownOutlined
                            className={`text-xs transition-transform ${isSubmenuOpen ? 'rotate-180' : ''}`}
                          />
                        </button>

                        {isSubmenuOpen && (
                          <>
                            {/* Backdrop invisible: click afuera cierra */}
                            <div className="fixed inset-0 z-30" onClick={() => setOpenSubmenu(null)} />
                            <div className="absolute top-full left-0 pt-1 w-48 z-40">
                              <div className="bg-white dark:bg-dark-bg rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1">
                                {item.submenu.map((subItem, subIndex) => {
                                  const isSubActive = currentPage === subItem.path.split('/')[1];
                                  const isAction = subItem.isAction;
                                  return (
                                    <button
                                      key={subIndex}
                                      onClick={() => {
                                        navigate(subItem.path);
                                        setOpenSubmenu(null);
                                      }}
                                      className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                                        isAction
                                          ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 font-medium'
                                          : isSubActive
                                          ? 'text-black dark:text-white bg-gray-100 dark:bg-gray-700 font-medium'
                                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      {subItem.icon}
                                      <span>{subItem.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'text-black dark:text-white bg-gray-100 dark:bg-gray-800'
                          : 'text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Derecha */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Buscador ⌘K */}
              <button
                onClick={() => setPaletteOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                title="Buscar (⌘K)"
              >
                <SearchOutlined />
                <span className="hidden xl:inline">Buscar…</span>
                <kbd className="hidden xl:inline text-[10px] font-medium border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5">
                  ⌘K
                </kbd>
              </button>

              <div className="hidden sm:flex items-center gap-2">
                <LanguageSelector />
                <ThemeSelector />
              </div>

              {/* Usuario (desktop) */}
              <div className="hidden lg:flex items-center gap-3 pl-2 sm:pl-4 border-l border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-dark-bg transition-colors"
                  >
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {user?.name || 'User'}
                      </p>
                    </div>
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm cursor-pointer hover:shadow-lg transition-shadow">
                      {user?.name?.charAt(0).toUpperCase() ||
                        user?.email?.charAt(0).toUpperCase() ||
                        'U'}
                    </div>
                  </button>

                  {showUserMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                      <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-dark-bg rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-20">
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {user?.name || 'User'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {user?.email || ''}
                          </p>
                          {user?.tenantId && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              Tenant ID: {user.tenantId.replace(/^tenant-/, '')}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/settings');
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <SettingOutlined />
                          Settings
                        </button>
                        <hr className="my-1 border-gray-200 dark:border-gray-700" />
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                        >
                          <LogoutOutlined />
                          Sign out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Hamburguesa (mobile) */}
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Abrir menú"
              >
                <MenuOutlined className="text-lg" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Drawer mobile */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-white dark:bg-gray-900 shadow-xl flex flex-col">
            {/* Header del drawer */}
            <div className="flex items-center justify-between px-4 h-16 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {user?.name?.charAt(0).toUpperCase() ||
                    user?.email?.charAt(0).toUpperCase() ||
                    'U'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email || ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Cerrar menú"
              >
                <CloseOutlined />
              </button>
            </div>

            {/* Nav del drawer */}
            <div className="flex-1 overflow-y-auto py-2">
              {visibleNavigationItems.map((item, index) => {
                const isActive = isItemActive(item);
                if (item.submenu) {
                  const isOpen = mobileSubmenu === index;
                  return (
                    <div key={index}>
                      <button
                        onClick={() => setMobileSubmenu(isOpen ? null : index)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium ${
                          isActive
                            ? 'text-black dark:text-white'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                        <DownOutlined
                          className={`ml-auto text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {isOpen && (
                        <div className="bg-gray-50 dark:bg-gray-800/50">
                          {item.submenu.map((subItem, subIndex) => {
                            const isSubActive = currentPage === subItem.path.split('/')[1];
                            return (
                              <button
                                key={subIndex}
                                onClick={() => navigateAndCloseMobile(subItem.path)}
                                className={`w-full flex items-center gap-3 pl-12 pr-4 py-2.5 text-sm ${
                                  subItem.isAction
                                    ? 'text-blue-600 dark:text-blue-400 font-medium'
                                    : isSubActive
                                    ? 'text-black dark:text-white font-medium'
                                    : 'text-gray-600 dark:text-gray-400'
                                }`}
                              >
                                {subItem.icon}
                                <span>{subItem.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <button
                    key={index}
                    onClick={() => navigateAndCloseMobile(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium ${
                      isActive ? 'text-black dark:text-white' : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                );
              })}

              <hr className="my-2 border-gray-200 dark:border-gray-800" />

              <button
                onClick={() => navigateAndCloseMobile('/settings')}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                <SettingOutlined />
                <span>Settings</span>
              </button>
            </div>

            {/* Footer del drawer: idioma / tema / salir */}
            <div className="border-t border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center justify-between mb-2">
                <LanguageSelector />
                <ThemeSelector />
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <LogoutOutlined />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
};

export default TopNavigation;
